import { MultiSelect } from 'district-ui-client/components/multi-select'
import { service } from '@ember/service'
import { t, type IntlService } from 'ember-intl'
import { TooltipInfo } from 'district-ui-client/components/tooltip'
import Component from '@glimmer/component'
import type { LessonLevelDomain } from 'district-ui-client/services/mx-lesson-level-domain'
import type MxLessonLevelDomainService from 'district-ui-client/services/mx-lesson-level-domain'
import type { SelectOption } from '@blakeelearning/ember-select'
import type SessionService from 'district-ui-client/services/session'
import type GradeSetsService from 'district-ui-client/services/grade-sets'

interface MxLessonLevelDomainPickerSignature {
  Args: {
    selected: string[]
    onChange: (selected: SelectOption['value'][]) => void
  }
}

export class MxLessonLevelDomainPicker extends Component<MxLessonLevelDomainPickerSignature> {
  @service mxLessonLevelDomain!: MxLessonLevelDomainService

  @service gradeSets!: GradeSetsService

  @service session!: SessionService

  @service intl!: IntlService

  get selected() {
    if (this.args.selected.length === 0) {
      return this.records.map((gradeDomain) => gradeDomain.id)
    }
    return this.args.selected
  }

  get records(): LessonLevelDomain[] {
    return this.mxLessonLevelDomain.records
  }

  get standardsSet() {
    return this.session.currentDistrict.standardsSet
  }

  get hasStandardsSet() {
    return Boolean(this.standardsSet)
  }

  get options() {
    return this.records
      .sort((a, b) => a.re_grade_position - b.re_grade_position || a.title.localeCompare(b.title))
      .map((gradeDomain) => {
        const gradeNameShort = this.gradeNameShort(gradeDomain.re_grade_position)
        const gradeNameFull = this.gradeNameFull(gradeDomain.re_grade_position)
        return {
          label: gradeDomain.title,
          labelShort: `${gradeNameShort}.${gradeDomain.domain_code}`,
          value: gradeDomain.id,
          group: gradeNameFull,
        }
      })
  }

  get isDisabled() {
    return !this.hasStandardsSet
  }

  get tooltipText() {
    return this.isDisabled ? this.tooltipDisabledText : this.intl.t('reporting.scopes.gradeAndDomain.tooltip')
  }

  get tooltipDisabledText() {
    return `${this.intl.t('stateAcademicStandards.unableToLoadOptions')}
    ${this.intl.t('stateAcademicStandards.unset')}`
  }

  get placeholder() {
    switch (true) {
      case this.selected.length === 0:
      case this.selected.length === this.options.length:
        return this.allSelectedPlaceholder(this.options.length)
      case this.selected.length === 1:
        return this.oneSelectedPlaceholder(this.selected[0])
      default:
        return this.someSelectedPlaceholder(this.selected.length)
    }
  }

  allSelectedPlaceholder(count: number) {
    return this.intl.t('reporting.scopes.gradeAndDomain.allSelected', { count })
  }

  oneSelectedPlaceholder(selectedValue: string) {
    const gradeDomain = this.options.find((gradeDomain) => gradeDomain.value === selectedValue)
    if (gradeDomain) return gradeDomain.labelShort
    return this.someSelectedPlaceholder(1)
  }

  someSelectedPlaceholder(count: number) {
    return this.intl.t('reporting.scopes.gradeAndDomain.someSelected', { count })
  }

  gradeNameFull(gradePosition: number) {
    const { fullName } = this.gradeSets.findByPosition(gradePosition)
    return fullName ?? String(gradePosition)
  }

  gradeNameShort(gradePosition: number) {
    const { name } = this.gradeSets.findByPosition(gradePosition)
    return name ?? String(gradePosition)
  }

  <template>
    <label class="flex flex-col gap-y-1" data-test-grade-domain-picker>
      <div class="space-x-0.5 text-sm font-medium text-neutral-250">
        <span>{{t "reporting.scopes.gradeAndDomain.label"}}</span>
        <TooltipInfo data-test-grade-and-domain-info-tooltip class="text-neutral-300" @text={{this.tooltipText}} />
      </div>
      <MultiSelect
        @style="subtle"
        @placeholder={{this.placeholder}}
        @searchPlaceholder={{t "reporting.scopes.gradeAndDomain.searchPlaceholder"}}
        @selectAllLabel={{t "reporting.scopes.gradeAndDomain.selectAll" count=this.options.length}}
        @options={{this.options}}
        @selectedItems={{this.selected}}
        @onCommit={{@onChange}}
        @disabled={{this.isDisabled}}
      />
    </label>
  </template>
}
